// Sends a failed Keycloak handshake to the server log, in development only.
//
// A login loop wipes the browser console on every navigation, so the one piece
// of information that explains the failure can be gone before anyone reads it.
// The server log does not navigate. This exists so diagnosing a broken local
// login does not depend on catching a red line as it flashes past.
//
// Deliberately narrow:
//   - development hosts only, so nothing is ever reported from a real user's
//     browser;
//   - sendBeacon where available, because the page is usually mid-navigation
//     and a normal fetch would be cancelled;
//   - completely silent on failure — a diagnostic must never be the reason a
//     login breaks.

function isLocalDev () {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1'
}

// `TypeError: Failed to fetch` deliberately carries no URL -- that opacity is a
// privacy feature of the platform, and it is also why a blocked request is so
// hard to diagnose. So during the handshake ONLY, and on a dev host only, wrap
// fetch to record which URL failed. Returns a restore function; always call it.
export function instrumentFetch (deps = {}) {
  const {
    enabled = isLocalDev(),
    target = typeof window !== 'undefined' ? window : null,
    store = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  } = deps

  if (!enabled || !target || typeof target.fetch !== 'function') return () => {}

  const original = target.fetch
  target.fetch = async function instrumented (input, init) {
    try {
      return await original.call(this, input, init)
    } catch (e) {
      try {
        const url = typeof input === 'string' ? input : (input?.url || String(input))
        store?.setItem('auth_failed_fetch', `${url} :: ${e?.message || e}`)
      } catch { /* never let the probe break the request */ }
      throw e
    }
  }

  return () => { target.fetch = original }
}

export function lastFetchFailure (store = typeof sessionStorage !== 'undefined' ? sessionStorage : null) {
  try {
    return store?.getItem('auth_failed_fetch') || null
  } catch {
    return null
  }
}

export function reportAuthFailure (error, deps = {}) {
  const {
    enabled = isLocalDev(),
    beacon = typeof navigator !== 'undefined' && navigator.sendBeacon
      ? navigator.sendBeacon.bind(navigator)
      : null,
    post = typeof fetch !== 'undefined' ? fetch : null,
    href = typeof window !== 'undefined' ? window.location.href : '',
  } = deps

  if (!enabled) return false

  const payload = JSON.stringify({
    message: error?.message || String(error || 'unknown'),
    name: error?.name || null,
    // The error keycloak-js throws is often a bare object rather than an Error,
    // so stringify whatever else is on it; this is a local log line, not a UI.
    detail: safeDetail(error),
    // Which URL actually failed. This is the field that turns "Failed to fetch"
    // into an answer.
    failed_fetch: lastFetchFailure(),
    href,
  })

  const url = '/agents/api/v1/client_auth_errors'

  try {
    if (beacon) return Boolean(beacon(url, new Blob([payload], { type: 'application/json' })))
    if (post) {
      post(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true })
      return true
    }
  } catch {
    // Silent by design.
  }
  return false
}

function safeDetail (error) {
  if (!error || typeof error !== 'object') return null
  try {
    const plain = {}
    for (const key of Object.keys(error)) {
      const value = error[key]
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        plain[key] = value
      }
    }
    return Object.keys(plain).length ? plain : null
  } catch {
    return null
  }
}
